<template>
    <div :class="classes" :style="{ width: componentWidth }" @click="toggleEditMode">
        <CompassSkeleton v-show="loading" size="x-large" />
        <div v-show="!loading" ref="dropdownTrigger" class="edit-multiple-users__content">
            <CompassMultiAvatar
                v-if="formattedSharedStaff.length > 0"
                size="small"
                :avatars="formattedSharedStaff"
            />
            <CompassTooltip
                v-else
                :arrow="false"
                :content="$t.__('overview.opportunity.editMultipleUsers.assign_workers')"
            >
                <div class="edit-multiple-users__empty">
                    <CompassAvatar
                        size="small"
                        initials=""
                        photo="/ressources/logged/img/illustrations/avatar.svg"
                    />
                    <div class="edit-multiple-users__empty-add">
                        <CompassIcon icon="plus" size="small" />
                    </div>
                </div>
            </CompassTooltip>
        </div>
        <CompassDropdown
            v-model="selectedUsers"
            v-click-outside="handleCancel"
            parentRef="dropdownTrigger"
            :isVisible="editMode"
            filterable
            :appliedButton="$t.__('common.actions.apply')"
            :noResultsLabel="$t.__('common.labels.no_result')"
            :filterablePlaceholder="$t.__('common.actions.search')"
            :multiple="multiple"
            @onApplied="handleValueUpdate"
            @change="handleValueChange"
        >
            <CompassDropdownOption
                v-for="staff in formattedStaff"
                :key="staff.value"
                :value="staff.value"
                :label="staff.fullname"
                :selected="staff.selected"
                type="avatar"
                :avatar="staff.avatar"
            />
        </CompassDropdown>
    </div>
</template>
<script>
import { randAvatarColorFullname } from 'utils/avatar';
import { createNamespacedHelpers } from 'vuex';
import { isEqual } from 'lodash';
const { mapGetters } = createNamespacedHelpers('staffs');

export default {
    props: {
        data: {
            type: Array,
            required: true,
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editMode: false,
            selectedUsers: [],
            observer: null,
            componentWidth: 'auto',
        };
    },
    computed: {
        ...mapGetters({
            staff: 'getStaffs',
        }),
        classes() {
            return {
                'edit-multiple-users': true,
                'edit-multiple-users--editable': this.canEdit,
            };
        },
        formattedSharedStaff() {
            return this.data
                .filter((item) => item.status === 'ok')
                .map((staff) => {
                    return {
                        fullname: `${staff.firstname} ${staff.lastname}`,
                        value: staff.id,
                        size: 'small',
                        selected: this.isSelected(staff),
                        initials: this.getUserInitials(staff),
                        avatarColorType: this.randAvatarColorFullname(
                            `${staff.firstname} ${staff.lastname}`,
                        ),
                        photo: this.getUserPhoto(staff),
                        hoverable: this.canEdit,
                    };
                }, {});
        },
        formattedStaff() {
            return this.staff
                .filter((item) => item.status === 'ok')
                .map((item) => {
                    return {
                        fullname: `${item.firstname} ${item.lastname}`,
                        value: item.id,
                        selected: this.isSelected(item),
                        avatar: {
                            initials: this.getUserInitials(item),
                            colorType: this.randAvatarColorFullname(
                                `${item.firstname} ${item.lastname}`,
                            ),
                            photo: item.avatar,
                        },
                    };
                }, {});
        },
    },
    watch: {
        loading(newValue) {
            if (!newValue) {
                this.componentWidth = 'auto';
            }
        },
        data(newValue) {
            this.selectedUsers = newValue.map((staff) => +staff.id);
        },
    },
    created() {
        this.selectedUsers = this.data.map((staff) => +staff.id);
        window.addEventListener('keydown', this.handleKeyDown);
    },
    onBeforeDestroy() {
        window.removeEventListener('keydown', this.handleKeyDown);
    },
    methods: {
        randAvatarColorFullname,
        handleKeyDown($event) {
            if ($event.key === 'Escape') {
                this.handleCancel();
            }
        },
        handleCancel($event = null) {
            if (!$event || !$event.target.closest('.edit-multiple-users')) {
                this.editMode = false;
                return;
            }
        },
        isSelected(staff) {
            return this.selectedUsers.includes(staff.id);
        },
        getUserInitials(staff) {
            return `${staff.firstname.charAt(0)}${staff.lastname.charAt(0)}`;
        },
        getUserPhoto(staff) {
            if (
                !staff.avatar ||
                (typeof staff.avatar === 'object' && Object.keys(staff.avatar).length === 0)
            ) {
                return null;
            }
            if (typeof staff.avatar === 'string') {
                return staff.avatar;
            }
            if (staff.avatar.type === 'picture') {
                return staff.avatar.value;
            }
            return null;
        },
        toggleEditMode() {
            if (!this.editMode && this.canEdit && this.staff?.length > 0) {
                this.editMode = true;
            }
        },
        handleValueChange(value) {
            if (!this.multiple && !isEqual(value, this.selectedUsers)) {
                this.handleValueUpdate(value);
            }
        },
        handleValueUpdate(value = null) {
            this.$emit('update', value ?? this.selectedUsers);
            this.componentWidth = `${this.$refs.dropdownTrigger.offsetWidth}px`;
            this.editMode = false;
        },
    },
};
</script>
<style lang="scss" scoped>
.edit-multiple-users {
    &--editable {
        .edit-multiple-users__content {
            cursor: pointer;
        }
    }

    &__empty {
        position: relative;

        &::v-deep {
            .compass-avatar {
                opacity: 0.25;
                position: relative;
            }
        }

        &-add {
            align-items: center;
            background: var(--colors-common-background-primary-default);
            border-radius: 50%;
            bottom: 0;
            color: var(--colors-common-text-standard-on-color);
            display: flex;
            height: 20px;
            justify-content: center;
            position: absolute;
            right: 0;
            width: 20px;
        }

        &:hover {
            &-add {
                background: var(--colors-common-background-primary-hover);
            }
        }
    }

    &__content {
        align-items: center;
        cursor: default;
        display: flex;
        gap: var(--spacing-1);
        padding: 0;
        user-select: none;

        &::v-deep {
            .compass-avatar {
                flex-shrink: 0;
            }
        }
    }
}
</style>
